import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import {
  getCurrentApplicationId,
  previewModeSelector,
} from "selectors/editorSelectors";
import {
  isAnvilDebugEnabled,
  setAnvilDebug,
} from "../../common/logging/anvilDebug";
import type { AnvilCommand } from "./types";

/**
 * Builds the Anvil command set wired to existing, safe editor actions.
 * Kept intentionally small for the M3.1 MVP (preview toggle + deploy); extend
 * with navigation / add-widget commands in later increments.
 */
export function useAnvilCommands(): AnvilCommand[] {
  const dispatch = useDispatch();
  const applicationId = useSelector(getCurrentApplicationId);
  const isPreviewMode = useSelector(previewModeSelector);

  return useMemo<AnvilCommand[]>(
    () => [
      {
        id: "toggle-preview",
        label: isPreviewMode ? "Exit preview mode" : "Toggle preview mode",
        group: "Editor",
        keywords: ["preview", "view"],
        // Mirrors setPreviewModeInitAction (actions/editorActions); inlined to
        // avoid pulling that module's import graph into this Anvil-editor leaf
        // (cyclic-dep break). Keep the payload shape in lockstep with it.
        run: () =>
          dispatch({
            type: ReduxActionTypes.SET_PREVIEW_MODE_INIT,
            payload: !isPreviewMode,
          }),
      },
      {
        id: "deploy",
        label: "Deploy application",
        group: "Editor",
        keywords: ["publish", "ship", "release"],
        // The deploy flow surfaces its own progress/result feedback.
        silentToast: true,
        // Mirrors publishAnvilApplication (ce/actions/applicationActions);
        // inlined for the same cyclic-dep reason as toggle-preview above. Keep
        // the payload shape in lockstep with it.
        run: () =>
          dispatch({
            type: ReduxActionTypes.PUBLISH_ANVIL_APPLICATION_INIT,
            payload: { applicationId },
          }),
      },
      {
        id: "toggle-anvil-debug",
        label: "Toggle Anvil debug logging",
        group: "Developer",
        keywords: ["debug", "log", "logging", "verbose", "diagnostics"],
        run: () => setAnvilDebug(!isAnvilDebugEnabled()),
      },
    ],
    [dispatch, applicationId, isPreviewMode],
  );
}
